import { useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, Box } from '@mui/material';
import { useMaterialReactTable, MaterialReactTable } from 'material-react-table';
import { arraysEqual } from '../../../../utils/collections';
import { tableSelectionOptions, defaultTableOptions } from '../../../util/data-table';
import {
  equipmentFeedsCol,
  equipmentFeedsId,
  equipmentColumns,
  equipmentNameId,
  lastPublishedCol,
  equipmentTypeId,
  enrichColHeaderWithTooltip,
  enrichColAccessorFnWithMissing,
} from '../../columns';
import { createLink, deleteLink } from '../../../../redux/actions/buildings';

function hasLink(relationship, linkType) {
  return Object.hasOwn(relationship.link_types, linkType);
}

const TargetsEquipmentTable = ({
  linkType,
  linkTypes,
  stagingEquipment,
  equipment,
  canWrite,

  equipmentTypes,
  linkCreate,
  linkDelete,
}) => {
  const rowSelection = useMemo(
    () =>
      Object.fromEntries(
        equipment.published?.equipmentRelationships?.targets
          ?.filter((r) => hasLink(r, linkType))
          ?.map((r) => [r.equipment_id, true]) ?? []
      ),
    [linkType, equipment]
  );
  const columns = useMemo(
    () =>
      [
        linkType === linkTypes[0]
          ? enrichColAccessorFnWithMissing(enrichColHeaderWithTooltip(equipmentFeedsCol(equipment)))
          : null,
        ...equipmentColumns(false, equipmentTypes),
      ].filter(Boolean),
    [equipmentTypes, equipment, linkType, linkTypes]
  );
  const data = useMemo(
    // Default order selected first
    () =>
      stagingEquipment
        .filter((e) => Boolean(e.published))
        .filter((e) => e.equipId !== equipment.equipId)
        .sort((a, b) => {
          if (
            a.published?.equipmentRelationships?.sources
              ?.filter((r) => hasLink(r, linkType))
              ?.map((r) => r.equipment_id)
              ?.includes(equipment.published.id)
          ) {
            if (
              b.published?.equipmentRelationships?.sources
                ?.filter((r) => hasLink(r, linkType))
                ?.map((r) => r.equipment_id)
                ?.includes(equipment.published.id)
            ) {
              return a.id < b.id;
            }
            return -1;
          }
          if (
            b.published?.equipmentRelationships?.sources
              ?.filter((r) => hasLink(r, linkType))
              ?.map((r) => r.equipment_id)
              ?.includes(equipment.published.id)
          )
            return 1;
          return a.id < b.id;
        }),
    [stagingEquipment, equipment, linkType]
  );

  const mrt = useMaterialReactTable({
    ...defaultTableOptions,
    ...tableSelectionOptions,
    columns,
    data,
    initialState: {
      showColumnFilters: true,
      columnVisibility: Object.fromEntries(
        columns
          .map((c) => c.id)
          .filter(
            (id) =>
              ![equipmentFeedsId, equipmentNameId, equipmentTypeId, lastPublishedCol.id].includes(
                id
              )
          )
          .map((id) => [id, false])
      ),
      rowSelection,
    },
    getRowId: (e) => e.published.id,
    enableRowSelection: () => canWrite,
    renderTopToolbarCustomActions: ({ table }) => {
      const handleApply = () => {
        const selectedIds = table
          .getSelectedRowModel()
          .flatRows.map(({ original }) => original.published.id);
        function handleRemove() {
          const equipmentToRemove = equipment.published.equipmentRelationships?.targets
            ?.filter((r) => hasLink(r, linkType))
            ?.map((r) => r.equipment_id)
            ?.filter((eid) => !selectedIds.includes(eid));
          if (equipmentToRemove?.length > 0) return linkDelete(equipmentToRemove, linkType);
          return Promise.resolve();
        }
        function handleAdd() {
          const equipmentToAdd = selectedIds.filter(
            (l) =>
              !equipment.published.equipmentRelationships?.targets
                ?.filter((r) => hasLink(r, linkType))
                ?.map((r) => r.equipment_id)
                ?.includes(l)
          );
          if (equipmentToAdd?.length > 0) return linkCreate(equipmentToAdd, linkType);
          return Promise.resolve();
        }
        return handleRemove().then(() => handleAdd());
      };

      return (
        <Box sx={{ display: 'flex', gap: '0.5rem' }}>
          <Button
            color="success"
            disabled={
              !canWrite ||
              arraysEqual(
                table
                  .getSelectedRowModel()
                  .flatRows.map(({ original }) => original.published.id)
                  .toSorted(),
                equipment.published.equipmentRelationships?.targets
                  ?.filter((r) => hasLink(r, linkType))
                  ?.map((r) => r.equipment_id)
                  .toSorted()
              )
            }
            onClick={handleApply}
            variant="contained"
          >
            Apply Selection
          </Button>
        </Box>
      );
    },
  });
  return <MaterialReactTable table={mrt} />;
};

function mapStateToProps(state, { building }) {
  const {
    entities: { equipTypes },
  } = state;
  return {
    equipmentTypes: equipTypes.byId,
    canWrite: building.canWrite,
  };
}

function mapDispatchToProps(dispatch, { equipment, building }) {
  return {
    linkCreate: (equipmentToAdd, linkType) =>
      dispatch(
        createLink(building.id, linkType, equipment.publishedEquipmentId, [], equipmentToAdd)
      ),
    linkDelete: (equipmentToRemove, linkType) =>
      dispatch(
        deleteLink(building.id, linkType, equipment.publishedEquipmentId, [], equipmentToRemove)
      ),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(TargetsEquipmentTable);
